import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CountryProvider } from '@/contexts/CountryContext';
import Navbar from '@/components/zera/Navbar';
import Footer from '@/components/zera/Footer';
import PageHero from '@/components/zera/PageHero';
import ZeraPartners from '@/components/zera/ZeraPartners';
import {
  romanianExperiences,
  romaniaExperiencesIntro,
} from '@/data/romaniaExperiences';
import { ArrowRight } from 'lucide-react';

/**
 * Complete Romanian Experiences page — renders all ten signature
 * experiences from the shared data module. Each block carries an
 * id={slug} anchor so the homepage "Learn More" buttons deep-link
 * straight to the matching experience (/romania/experiences#slug).
 */
const RomaniaExperiencesInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to the anchored experience when arriving with a #hash.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait a tick so images/layout have mounted before measuring.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <div className="theme-romania min-h-screen bg-[#FAF8F3]">
      <Navbar />

      <PageHero
        image="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406729379_e48b2fcd.jpg"
        imageAlt="The winding Transfăgărășan mountain road through the Carpathian Mountains of Romania."
        title={`Signature\nRomania`}
        subtitle="Ten handcrafted ways to live Romania — luxury castles, Dracula legends, Carpathian adventures, thermal baths, wine cellars and wild bears — each curated by our Romanian travel experts."
        ctaText="Plan Your Journey"
        ctaLink="/romania/booking"
      />

      {/* Experiences */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px] bg-[#C9A961]" />
              <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Curated Journeys</span>
              <div className="w-8 h-[1px] bg-[#C9A961]" />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-[#1A3B5C] mb-6">
              Ten Ways to Experience Romania
            </h2>
            <p className="font-sans text-sm text-[#777] max-w-2xl mx-auto leading-relaxed">
              {romaniaExperiencesIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {romanianExperiences.map((exp, idx) => (
              <div
                key={exp.slug}
                id={exp.slug}
                className={`group flex flex-col scroll-mt-28 ${idx % 2 === 1 ? 'md:mt-16' : ''}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden mb-6">
                  <img
                    src={exp.image}
                    alt={exp.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5">
                    <span className="font-sans text-[10px] tracking-wider uppercase text-[#1A3B5C]">
                      {String(idx + 1).padStart(2, '0')} / {String(romanianExperiences.length).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-2">{exp.tagline}</span>
                <h3 className="font-serif text-2xl text-[#1A3B5C] font-light mb-3 group-hover:text-[#C9A961] transition-colors">
                  {exp.title}
                </h3>
                <p className="font-sans text-sm text-[#666] leading-relaxed mb-5">{exp.description}</p>
                <button
                  onClick={() => navigate('/romania/booking')}
                  className="self-start font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] flex items-center gap-2 hover:gap-3 transition-all"
                >
                  Reserve This Experience <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#1A3B5C] py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-light text-white mb-4">
            Craft Your Own Romanian Journey
          </h2>
          <p className="font-sans text-sm text-white/70 max-w-xl mx-auto mb-8">
            Our concierge team will tailor any of these experiences — or combine them — into one seamless bespoke itinerary across Romania.
          </p>
          <button
            onClick={() => navigate('/romania/booking')}
            className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all duration-300"
          >
            Begin Planning
          </button>
        </div>
      </section>

      <ZeraPartners />
      <Footer />
    </div>
  );
};

const RomaniaExperiences: React.FC = () => (
  <CountryProvider country="romania">
    <RomaniaExperiencesInner />
  </CountryProvider>
);

export default RomaniaExperiences;
